<template>
  <MetaTags v-if="metaInfo" :meta-info="metaInfo" />
  <div :class="`main-section ${sectionId}`">
    <!-- Page hero -->
    <hero
      v-if="imageUrl"
      :title="title"
      :sub-title="subTitle"
      :seo-title="seoTitle"
      :icon-class="iconClass"
      :image-url="imageUrl"
      :hero-link-text="heroLinkText"
      :hero-link-url="heroLinkUrl"
      :hero-link-type="heroLinkType"
      :selectedTab="sectionId"
      :noSearchBar="noSearchBar"
      :isSmallHero="isSmallHero"
      :reverse-titles="reverseTitles"
      :hero-link-navigate-to-contact-page="heroLinkNavigateToContactPage"
      :hasAgencySearchBar="hasAgencySearchBar"
    ></hero>

    <sticky-navigation
      v-if="stickyNavLinks"
      :anchor-links="stickyNavLinks.anchorLinks"
      :contact-links="stickyNavLinks.contactLinks"
    ></sticky-navigation>

    <!-- Variable content -->
    <div
      v-for="(section, index) in sections"
      :key="section.sectionId + index"
      :class="['section-wrapper', section.bgColor]"
    >
      <call-to-action
        :key="section.sectionId + index"
        v-if="section.sectionId === 'call-to-action'"
        :actions="section.content"
        :extraClass="section.extraClass"
      ></call-to-action>
      <ingress
        :key="section.sectionId + index"
        v-if="section.sectionId === 'ingress' && section.content"
        :ingress-text="section.content"
      ></ingress>
      <basic-article
        :key="section.sectionId + index"
        v-if="section.sectionId === 'basic-article' && section.content"
        :article="section.content"
        :bottomLink="section.bottomLink"
      ></basic-article>

      <job-listings :key="section.sectionId + index" v-if="section.sectionId === 'job-listings'"></job-listings>

      <three-cards
        :key="section.sectionId + index"
        v-if="section.sectionId === 'three-cards'"
        :content="section.content"
        :customClass="section.customClass"
        :bottomLink="section.bottomLink"
      ></three-cards>

      <current-news-and-text
        :key="section.sectionId + index"
        v-if="section.sectionId === 'current-news-and-text'"
        :current-news-items="section.content.newsItems"
        :class="section.className"
      ></current-news-and-text>

      <apartment-listings
        :key="section.sectionId + index"
        v-if="section.sectionId === 'apartment-listings'"
        :filters="section.content.filters"
        :newHousingCompaniesOnly="section.newHousingCompaniesOnly"
        :heading="section.content.heading"
        :link-title="section.content.linkTitle"
        :link-query="section.content.linkQuery"
      ></apartment-listings>

      <top-agencies :key="section.sectionId + index" v-if="section.sectionId === 'top-agencies'"></top-agencies>

      <agency-contact-embedded
        :key="section.sectionId + index"
        v-if="section.sectionId === 'agency-contact-embedded'"
        :content="section.content"
      ></agency-contact-embedded>
    </div>

    <dialogue identifier="contact-order-evaluation">
      <template #dialogueContent>
        <agency-contact formVersion="evaluation"></agency-contact>
      </template>
    </dialogue>
    <dialogue identifier="contact-full">
      <template #dialogueContent>
        <agency-contact></agency-contact>
      </template>
    </dialogue>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, type PropType } from 'vue';
// Loading common top-of-the-page components synchronously to avoid flickering
import MetaTags from '@/app/components/MetaTags.vue';
import Hero from '@/app/components/main-section/hero.vue';
import CallToAction from './call-to-action.vue';
import Ingress from './ingress.vue';
import BasicArticle from './basic-article.vue';
// AgencyContactEmbedded is imported synchronously to enable hash scrolling to contact form
import AgencyContactEmbedded from '@/app/components/agency-contact-embedded/agency-contact-embedded.vue';
import type { MetaInfo } from '@/types/metaInfo';
import type { SectionsType } from '@/schemas/landingPageSchema';

export default defineComponent({
  name: 'main-section',
  props: {
    metaInfo: {
      type: Object as PropType<MetaInfo>,
      required: false,
    },
    sectionId: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      required: false,
    },
    seoTitle: {
      type: String,
      required: false,
    },
    reverseTitles: {
      type: Boolean,
      required: false,
    },
    iconClass: {
      type: String,
      required: false,
    },
    imageUrl: {
      type: String,
      required: true,
    },
    heroLinkText: {
      type: String,
      required: false,
    },
    heroLinkType: {
      type: String,
      required: false,
    },
    heroLinkUrl: {
      type: String,
      required: false,
    },
    noSearchBar: {
      type: Boolean,
      required: false,
    },
    sections: {
      type: Array as PropType<SectionsType[]>,
      required: true,
    },
    stickyNavLinks: {
      type: Object,
      required: false,
    },
    isSmallHero: {
      type: Boolean,
      required: false,
    },
    heroLinkNavigateToContactPage: {
      type: Boolean,
      required: false,
    },
    hasAgencySearchBar: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    MetaTags,
    Hero,
    BasicArticle,
    Ingress,
    CallToAction,
    AgencyContactEmbedded,
    StickyNavigation: defineAsyncComponent(() => import('@/app/components/sticky-navigation/sticky-navigation.vue')),
    ApartmentListings: defineAsyncComponent(() => import('@/app/components/apartment-listings/apartment-listings.vue')),
    AgencyContact: defineAsyncComponent(() => import('@/app/components/agency-contact/agency-contact.vue')),
    TopAgencies: defineAsyncComponent(() => import('@/app/components/top-agencies/top-agencies.vue')),
    Dialogue: defineAsyncComponent(() => import('@/app/components/dialogue/dialogue.vue')),
    ThreeCards: defineAsyncComponent(() => import('@/app/components/three-cards/three-cards.vue')),
    CurrentNewsAndText: defineAsyncComponent(
      () => import('@/app/components/current-news-and-text/current-news-and-text.vue')
    ),
    JobListings: defineAsyncComponent(() => import('@/app/components/job-listings/job-listings.vue')),
  },
});
</script>
